import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output, signal, WritableSignal } from '@angular/core';
import { ChatLoadingModel, LOADING_INFO, PROMPT_MSG_TYPES } from 'countable@model';
import { interval, Observable, Subject, take, takeUntil, takeWhile, timer } from 'rxjs';
import { map, scan, tap } from 'rxjs/operators';

@Component({
  selector: 'app-ai-loading-state',
  templateUrl: './ai-loading-state.component.html',
  styleUrl: './ai-loading-state.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AiLoadingStateComponent implements OnDestroy {

  @Input() set loadingState(info: ChatLoadingModel) {
    this.setState(info);
  }

  @Output() showActionItems: EventEmitter<boolean> = new EventEmitter<boolean>(true);

  public loadingType: WritableSignal<PROMPT_MSG_TYPES> = signal<PROMPT_MSG_TYPES>(null);
  public loadingText: WritableSignal<string> = signal('Gathering data...');
  public loadType: WritableSignal<'STREAM' | 'LOADING' | 'NONE'> = signal<'STREAM' | 'LOADING' | 'NONE'>('NONE');
  public streamText: WritableSignal<string> = signal<string>('');
  public inputText: WritableSignal<string> = signal<string>('');
  private typingInterval: any;
  private cancelRequest$: Subject<void> = new Subject<void>();

  private setState(info: ChatLoadingModel) {
    this.loadingType.set(info?.promptType);
    this.loadType.set(info?.loadingType);
    if (info?.loadingType === 'LOADING') {
      this.triggerLoadingMessages(this.loadingType());
    }
    if (info?.loadingType === 'STREAM') {
      this.inputText.set(info?.text);
      this.streamTypingEffect(info?.text);
    }
    if (info?.loadingType === 'NONE' && info?.text) {
      this.inputText.set(info?.text);
      this.showActionItems.emit(true);
    }
  }

  private triggerLoadingMessages(type: PROMPT_MSG_TYPES = 'FR') {
    const loadingInfo = structuredClone(LOADING_INFO.find(l => l?.promptType === type));
    const intervalDuration = loadingInfo?.intervalTime;
    const totalSteps = loadingInfo?.steps?.length ?? 0;

    timer(0, intervalDuration).pipe(
      take(totalSteps),
      map(i => {
        const messages = loadingInfo?.steps[i];
        const randomIndex = Math.floor(Math.random() * messages.length);
        return messages[randomIndex];
      })
    ).subscribe(message => {
      this.loadingText.set(message);
    });

    timer(totalSteps * intervalDuration).subscribe(() => {
      this.loadingText.set('Fetching Output...');
    });
  }

  private streamTypingEffect(message: string) {
    this.streamTyping(message, 15).pipe(
      takeUntil(this.cancelRequest$)
    ).subscribe({
      next: value => {
        this.streamText.set(value);
      }
    });
  }

  streamTyping(message: string, speed: number = 50): Observable<string> {
    return interval(speed).pipe(
      scan((acc, index) => acc + message[index], ''),
      takeWhile((typedMessage) => typedMessage.length - 1 < message.length),
      takeUntil(this.cancelRequest$)
    );
  }

  ngOnDestroy() {
    clearInterval(this.typingInterval);
    this.cancelRequest$.next();
    this.cancelRequest$.complete();
  }

}
